import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Company } from 'src/app/api/fn/generals/company.model';
import { CompanyService } from 'src/app/api/services/company.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  bootstrap123,
  bootstrapBriefcaseFill,
  bootstrapBuilding,
  bootstrapBuildings,
  bootstrapEnvelopeFill,
  bootstrapGeoAltFill,
  bootstrapGlobe,
  bootstrapHouseFill,
  bootstrapInfoCircle,
  bootstrapMapFill,
} from '@ng-icons/bootstrap-icons';
import { featherEdit2 } from '@ng-icons/feather-icons';
import { ToastService } from 'src/app/shared/service/toast.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { VerificationModalComponent } from 'src/app/shared/component/verification-modal/verification-modal.component';
import { LoadingComponent } from 'src/app/shared/component/loading/loading.component';
import { ImageUploadModalComponent } from 'src/app/shared/component/image-upload-modal/image-upload-modal.component';
import { APP_CONSTANTS } from 'src/app/core/constant/constants';
import { finalize } from 'rxjs';
import { UpdateEmailComponent } from 'src/app/shared/component/update-email-modal/update-email-modal.component';
import { handleApiError } from 'src/app/shared/util/handle-api-error';
import { CompanyStateService } from 'src/app/shared/service/company-state.service';

@Component({
  selector: 'app-user-company-details',
  imports: [
    CommonModule,
    NgIcon,
    LoadingComponent,
    ImageUploadModalComponent,
    UpdateEmailComponent,
  ],
  templateUrl: './user-company-details.component.html',
  viewProviders: [
    provideIcons({
      bootstrapEnvelopeFill,
      bootstrapBriefcaseFill,
      bootstrap123,
      bootstrapHouseFill,
      bootstrapBuildings,
      bootstrapGeoAltFill,
      bootstrapBuilding,
      bootstrapMapFill,
      bootstrapGlobe,
      featherEdit2,
      bootstrapInfoCircle,
    }),
  ],
})
export class UserCompanyDetailsComponent implements OnInit {
  @ViewChild('changeEmailModal')
  updateEmailModal!: UpdateEmailComponent;
  company: Company | null = null;

  selectedCompanyId!: string;
  selectedCompanyEmail!: string;

  isLoading: boolean = false;
  modalLoading: boolean = false;
  teamLoading: boolean = false;
  showModalUpdate: boolean = false;
  showImageTeamUpdate: boolean = false;
  editBasicModalOpen: boolean = false;
  showVerificationModal: boolean = false;
  editInstitutionalModalOpen: boolean = false;
  showVerificationModalWarning: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private toastService: ToastService,
    private companyState: CompanyStateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.isLoading = true;
    if (id) {
      this.companyService.getCompanyById(id).subscribe({
        next: (response) => {
          this.company = response.dto.company;
          this.companyState.setVerified(this.company.verified as '0' | '1');
          this.isLoading = false;
        },
        error: (err) => {
          this.toastService.showMessages(
            'error',
            err.error?.listMessage || [MESSAGES.COMPANY.GET_COMPANY_ERROR]
          );
          this.router.navigate(['/user/company']);
          this.isLoading = false;
        },
      });
    } else {
      this.toastService.showMessages('error', [MESSAGES.GENERAL.ID_INVALID]);
      this.router.navigate(['/user/company']);
    }

    this.companyState.verified$.subscribe((verified) => {
      this.company = { ...(this.company || {}), verified } as Company;
    });
  }

  openEditBasicModal() {
    this.editBasicModalOpen = true;
  }
  openEditInstitutionalModal() {
    this.editInstitutionalModalOpen = true;
  }

  navigateUpdate() {
    this.router.navigate(['/user/company', this.company?.idCompany, 'edit']);
  }

  openVerificationModal(email: string) {
    this.selectedCompanyEmail = email;
    this.showVerificationModal = true;
  }

  openModalUploadImage() {
    this.showModalUpdate = true;
  }

  openModalTeamUploadImage() {
    this.showImageTeamUpdate = true;
  }

  handleResend() {
    if (!this.company?.email) return;

    this.companyService
      .sendVerificationCode(this.company.email, this.company.idCompany)
      .pipe(
        handleApiError(this.toastService, MESSAGES.COMPANY.EMAIL_RESEND_ERROR)
      )
      .subscribe(() => {
        this.toastService.showMessages('success', [
          MESSAGES.COMPANY.EMAIL_RESEND_SUCCESS,
        ]);
      });
  }

  handleSendCode({ email }: { email: string }) {
    if (!this.company) return;

    this.companyService
      .sendVerificationCode(email, this.company.idCompany)
      .subscribe({
        next: (res) => {
          this.toastService.showMessages('success', res.listMessage);
          setTimeout(() => window.location.reload(), 1200);
        },
        error: (err) =>
          this.toastService.showMessages(
            'error',
            err.error?.listMessage || ['Error al enviar código']
          ),
      });
  }

  closeVerificationModal() {
    this.showVerificationModal = false;
  }

  uploadBanner(file: File) {
    if (!file || !this.company?.idCompany) return;

    this.modalLoading = true;

    this.companyService
      .updateUrlBannerMvv(this.company.idCompany, file)
      .pipe(
        handleApiError(this.toastService, MESSAGES.COMPANY.UPDATE_BANNER_ERROR),
        finalize(() => {
          this.modalLoading = false;
          this.showModalUpdate = false;
        })
      )
      .subscribe((res: any) => {
        this.toastService.showMessages('success', [
          MESSAGES.COMPANY.UPDATE_BANNER_SUCCESS,
        ]);
        if (this.company) {
          this.company.urlBannerMvv = res.dto.url;
        }
      });
  }

  uploadTeam(file: File) {
    if (!file || !this.company?.idCompany) return;

    this.teamLoading = true;

    this.companyService
      .updateUrlBannerOurHistory(this.company.idCompany, file)
      .pipe(
        handleApiError(this.toastService, MESSAGES.COMPANY.UPDATE_BANNER_ERROR),
        finalize(() => {
          this.teamLoading = false;
          this.showImageTeamUpdate = false;
        })
      )
      .subscribe((res: any) => {
        this.toastService.showMessages('success', [
          MESSAGES.COMPANY.UPDATE_BANNER_SUCCESS,
        ]);
        if (this.company) {
          this.company.urlBannerOurHistory = res.dto.url;
        }
      });
  }

  get bannerCompany() {
    return this.company?.urlBannerMvv ?? APP_CONSTANTS.DEFAULT_BANNER_COMPANY;
  }

  get teamImage() {
    return (
      this.company?.urlBannerOurHistory ?? APP_CONSTANTS.DEFAULT_TEAM_COMPANY
    );
  }
}
